import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';

import { GemHealthCheckBanner_GemFragment } from 'src/generated/mloop-graphql';

import AppWideBanner, { AppWideBannerAction } from '../AppWideBanner';

interface Fragments {
  gem: GemHealthCheckBanner_GemFragment;
}

interface Props {
  retry: AppWideBannerAction;
}

const GemHealthCheckBanner: FCWithFragments<Fragments, Props> = ({ gem, retry }) => {
  const { t } = useTranslation();
  // hide if its not installed or is healthy
  if (!gem || !gem?.isInstalled || gem?.isHealthy) {
    return null;
  }

  const endpoints = [
    !gem.canReadUsers && t('hiring-dashboard-nav:nav.banner.endpoint.listUsers'),
    !gem.canReadUser && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveUser'),
    !gem.canReadJobs && t('hiring-dashboard-nav:nav.banner.endpoint.listJobs'),
    !gem.canReadJob && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveJob'),
    !gem.canReadJobStages && t('hiring-dashboard-nav:nav.banner.endpoint.listJobStages'),
    !gem.canReadJobStageForJob && t('hiring-dashboard-nav:nav.banner.endpoint.listJobStageForJob'),
    !gem.canReadJobPostings && t('hiring-dashboard-nav:nav.banner.endpoint.listJobPostings'),
    !gem.canReadUserRoles && t('hiring-dashboard-nav:nav.banner.endpoint.listUserRoles'),
    !gem.canReadInterviews && t('hiring-dashboard-nav:nav.banner.endpoint.listInterviews'),
    !gem.canReadInterview && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveInterview'),
    !gem.canReadCandidates && t('hiring-dashboard-nav:nav.banner.endpoint.listCandidates'),
    !gem.canReadCandidate && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveCandidate'),
    !gem.canReadActivityFeed && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveActivityFeed'),
    !gem.canReadApplications && t('hiring-dashboard-nav:nav.banner.endpoint.listApplications'),
    !gem.canReadApplication && t('hiring-dashboard-nav:nav.banner.endpoint.retrieveApplication'),
  ].filter(Boolean) as string[];

  const text =
    endpoints.length > 0
      ? t('hiring-dashboard-nav:nav.banner.integrationUnhealthyWithEndpoints', {
          integrationName: 'Gem',
          endpoints,
        })
      : t('hiring-dashboard-nav:nav.banner.integrationUnhealthy', { integrationName: 'Gem' });

  return <AppWideBanner color="foreground" backgroundColor="error" text={text} action={retry} />;
};

GemHealthCheckBanner.fragments = {
  gem: gql`
    fragment GemHealthCheckBanner_gem on GemIntegrationHealth {
      isInstalled
      isHealthy
      canReadUsers
      canReadUser
      canReadJobs
      canReadJob
      canReadJobStages
      canReadJobStageForJob
      canReadJobPostings
      canReadUserRoles
      canReadInterviews
      canReadInterview
      canReadCandidates
      canReadCandidate
      canReadActivityFeed
      canReadApplications
      canReadApplication
    }
  `,
};

export default GemHealthCheckBanner;
